import { trackAppCarousel } from '@ecomm/cdp-tracking'
import {
  ContentfulImage,
  type ContentfulImageSchema
} from '@ecomm/contentful-components'
import { Trans } from '@ecomm/i18n'
import { useSwipeMotion } from '@ecomm/shared-hooks'
import { Fragment, useCallback, useState } from 'react'

import AppRating from './AppRating'
import { useOnTabClick } from './useOnTabClick'

type AppWidgetProps = {
  readonly data: AppWidgetData
}
export type AppWidgetData = {
  readonly tabs: ReadonlyArray<AppWidgetTab>
  readonly title?: JSX.Element | string
  readonly showAppRating?: boolean
}

export type AppWidgetTab = {
  readonly tab: string
  readonly title: string
  readonly description?: string
  readonly contents: ReadonlyArray<{
    readonly list: ReadonlyArray<string>
    readonly title?: string
  }>
  readonly image?: ContentfulImageSchema
  readonly disclaimer?: ReadonlyArray<string>
}

export function AppWidget({ data }: AppWidgetProps) {
  const onSwipeLeft = () => {
    handleTabClick((activeTab + 1) % tabs.length)
  }
  const onSwipeRight = () => {
    handleTabClick((activeTab - 1 + tabs.length) % tabs.length)
  }
  const touchEvents = useSwipeMotion({
    onSwipeLeft,
    onSwipeRight,
    sensitivity: 120
  })

  const { tabs, title, showAppRating } = data
  const [activeTab, setActiveTab] = useState<number>(0)

  const onTabClick = useOnTabClick()

  const handleTabClick = useCallback(
    (index: number) => {
      setActiveTab(index)
      onTabClick(tabs[index]?.tab || '')
      trackAppCarousel(tabs[index]?.tab.toLowerCase() || '')
    },
    [onTabClick, tabs]
  )

  const activeTabData = tabs[activeTab]

  return (
    <div
      className="prose md:prose-md lg:prose-lg relative w-full"
      data-component="AppWidget"
      data-testid="AppWidget"
    >
      <div
        className="absolute h-full w-full"
        data-component="TouchControls"
        {...touchEvents}
      />
      {title ? (
        <h2 className="mb-6 px-4 text-center md:mb-8 md:px-0">{title}</h2>
      ) : null}
      <div className="flex flex-col overflow-hidden rounded-2xl lg:flex-row-reverse">
        <div className="bg-neutral-light-50 pointer-events-none lg:w-5/12 lg:flex-1">
          <div className="space-end flex h-full mt-2 lg:mt-6 ">
            {!!activeTabData?.image ? (
              <ContentfulImage
                className="max-h-96 lg:h-full lg:max-h-none object-contain"
                {...activeTabData.image}
                style={{ objectFit: 'contain' }}
              />
            ) : null}
          </div>
        </div>
        <div className="bg-neutral-black flex min-h-[25rem] flex-col p-6 md:h-auto md:min-h-[28.75rem] md:p-8 lg:min-h-[40.5rem] lg:w-7/12 lg:p-12">
          <div className="mb-4 hidden w-full justify-around gap-6 md:flex md:px-4 lg:mb-6">
            {tabs.map((tab, index) => (
              <button
                className={`cursor-pointer border-0 bg-inherit px-3 pb-2 text-lg text-white ${
                  activeTab === index &&
                  ' border-primary-100 border-x-0 border-b-4 border-t-0 border-solid font-bold '
                }`}
                key={tab.tab}
                onClick={() => {
                  handleTabClick(index)
                }}
              >
                <span
                  className={activeTab === index ? 'opacity-100' : 'opacity-95'}
                >
                  {tab.tab}
                </span>
              </button>
            ))}
          </div>
          <div className="pointer-events-none grow">
            <div data-component="AppWidget-headline">
              <h3 className="mb-6 mt-0 text-white">{tabs[activeTab]?.title}</h3>
            </div>
            <div className="text-sm mt-4 opacity-90 md:text-xl">
              {tabs[activeTab]?.description ? (
                <p className="hidden text-white md:block">
                  {tabs[activeTab].description}
                </p>
              ) : null}
              {tabs[activeTab]?.contents.map((content, index) => (
                <Fragment key={`content-${index}`}>
                  {content.title ? (
                    <h4
                      className="mb-0 mt-6 text-xl font-bold text-white"
                      key={`title-${index}`}
                    >
                      {content.title}
                    </h4>
                  ) : null}
                  <ul
                    key={`list-${index}`}
                    className="my-4 list-disc pl-6 md:pl-8"
                  >
                    {content.list.map((text, index) => (
                      <li
                        className="text-body-size py-2 font-normal text-white md:py-3"
                        key={index}
                      >
                        <Trans defaults={text} />
                      </li>
                    ))}
                  </ul>
                </Fragment>
              ))}
              <ul className="m-0 flex list-none flex-col gap-3 p-0 text-xs text-white">
                {activeTabData?.disclaimer?.map((disclaimer, index) => (
                  <li
                    className="list-none text-xs font-normal text-white"
                    data-component={`AppWidget-disclaimer-${index}`}
                    key={index}
                  >
                    {disclaimer}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {showAppRating ? (
            <div className="relative bottom-0 hidden lg:block">
              <AppRating />
            </div>
          ) : null}
        </div>
      </div>
      <div className="relative my-4 flex flex-row justify-center gap-4 md:hidden">
        {tabs.map((tab, index) => (
          <button
            aria-label={`Slide ${index + 1} of ${tabs.length}`}
            className={`h-2 w-2 cursor-pointer rounded-full border-0 p-0 md:h-3 md:w-3 ${
              activeTab === index ? 'bg-neutral-black' : 'bg-[#D9D9D9]'
            }`}
            key={tab.tab}
            onClick={() => {
              setActiveTab(index)
            }}
          />
        ))}
      </div>
    </div>
  )
}
